import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { CheckCircle2, Mail, ShieldAlert, AlertTriangle } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

const publicApi = axios.create({ baseURL: API, withCredentials: true });

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    publicApi
      .get(`/invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This invite link is invalid or has expired."));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      toast.success(`Welcome to ${invite.program_name}!`);
      navigate(`/programs/${data.program_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not accept invite.";
      toast.error(detail);
      setError(detail);
    } finally {
      setAccepting(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="text-center" data-testid="invite-error">
          <ShieldAlert className="w-10 h-10 text-slate-400 mx-auto" />
          <h1 className="font-heading text-2xl mt-4">Invite unavailable</h1>
          <p className="text-slate-600 mt-2 text-sm">{error}</p>
          <Link to="/" className="inline-block mt-4 text-sm underline underline-offset-4 text-slate-700">
            Go to Array Engine
          </Link>
        </div>
      </Shell>
    );
  }
  if (!invite) {
    return <Shell><p className="text-slate-500" data-testid="invite-loading">Loading invite…</p></Shell>;
  }

  return (
    <Shell>
      <Toaster richColors />
      <div className="text-center" data-testid="invite-card">
        <div className="w-12 h-12 mx-auto bg-slate-900 text-white grid place-items-center rounded-sm">
          <span className="font-heading font-bold text-2xl leading-none">A</span>
        </div>
        <p className="label-overline mt-4">Program invite</p>
        <h1 className="font-heading text-3xl font-semibold mt-2 leading-tight" data-testid="invite-program-name">
          {invite.program_name}
        </h1>
        {invite.program_description && (
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">{invite.program_description}</p>
        )}
        <p className="text-sm text-slate-700 mt-6">
          <strong>{invite.invited_by_name || "An admin"}</strong> invited{" "}
          <span className="font-mono text-slate-900">{invite.email}</span> as a{" "}
          <strong className="capitalize">{invite.role}</strong>.
        </p>

        {/* CASE 1: not authenticated */}
        {!authLoading && !user && (
          <div className="mt-8 space-y-3" data-testid="invite-cta-anon">
            <Link to={`/register?invite=${token}&email=${encodeURIComponent(invite.email)}`} className="block">
              <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="invite-create-account-btn">
                Create your account to accept
              </Button>
            </Link>
            <Link to={`/login?invite=${token}`} className="block">
              <Button variant="outline" className="w-full" data-testid="invite-signin-btn">
                Already have an account? Sign in
              </Button>
            </Link>
          </div>
        )}

        {/* CASE 2: authenticated, email matches */}
        {!authLoading && user && user.email?.toLowerCase() === invite.email.toLowerCase() && (
          <div className="mt-8" data-testid="invite-cta-match">
            <Button
              onClick={accept}
              disabled={accepting}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="invite-accept-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {accepting ? "Joining…" : `Accept and join ${invite.program_name}`}
            </Button>
          </div>
        )}

        {/* CASE 3: authenticated as someone else */}
        {!authLoading && user && user.email?.toLowerCase() !== invite.email.toLowerCase() && (
          <div className="mt-8 space-y-3" data-testid="invite-cta-mismatch">
            <div className="border border-amber-200 bg-amber-50 text-amber-900 text-xs p-3 rounded-sm flex items-start gap-2 text-left">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                You're signed in as <strong>{user.email}</strong>, but this invite is for <strong>{invite.email}</strong>.
                Sign out and sign back in (or register) with the invited email to accept.
              </div>
            </div>
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full" data-testid="invite-switch-account-btn">
                <Mail className="w-4 h-4 mr-2" /> Switch account
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 p-10 rounded-sm">
        {children}
      </div>
    </div>
  );
}
